import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { canManageContractors, canOpenContractorAreaMap, canAccessContractorsPanel } from "@/features/contractors/utils";

export function TechnicalDirectorGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (user.role === "director" && !user.is_technical_director && !user.is_ceo) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// Management backlog — STRICT circle: only technical directors and the CEO
// pass; every other role (including regular employees) is redirected. Mirrors
// backend IsManagementCircle in apps/mgmt_backlog/views.py.
export function ManagementCircleGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  const inCircle =
    user.is_ceo || (user.role === "director" && user.is_technical_director);
  if (!inCircle) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Tenders panel — internal roles as before, but a non-technical director
// passes only with the CEO-granted 'Przetargi: widok' permission
// (has_view_tenders_permission). Mirrors the sidebar "Przetargi" section.
export function TendersAccessGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  const allowedRoles = ["director", "coordinator", "accountant", "employee"];
  if (!allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  if (
    user.role === "director" &&
    !user.is_technical_director &&
    !user.is_ceo &&
    !user.has_view_tenders_permission
  ) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// Contractors / framework agreements panels — the roles that always saw them
// (director, coordinator) plus anyone the CEO granted 'Podwykonawcy i umowy
// ramowe'. Read access only; writes are gated by ContractorsManageGuard and by
// backend CanManageContractors. Must stay aligned with the "Zamówienia
// podwykonawcze" section in components/layout/Sidebar.tsx.
export function ContractorsAccessGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  // Jedna reguła z canAccessContractorsPanel — megamapa chowa po niej link do karty.
  if (!canAccessContractorsPanel(user)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Megamapa podwykonawców — the contractors panel circle plus developers (only this page).
export function ContractorAreaMapGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!canOpenContractorAreaMap(user)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Creating / editing a contractor — mirrors backend CanManageContractors.
export function ContractorsManageGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!canManageContractors(user)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Requisitions / orders / protocols — mirrors backend HasOrdersAccess: a
// non-technical director passes only with the CEO-granted 'Zamawiający'
// permission. Must stay aligned with the "Zamówienia podwykonawcze" section
// visibility in components/layout/Sidebar.tsx.
export function OrdersAccessGuard() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!user) return <Navigate to="/auth/login" replace />;
  if (
    user.role === "director" &&
    !user.is_technical_director &&
    !user.is_ceo &&
    !user.has_orderer_permission
  ) {
    return <Navigate to="/" replace />;
  }
  // Pracownik wchodzi do ZAMÓWIEŃ tylko z nadanym dostępem (OrderAccessGrant) albo
  // jako zamawiający — backend odmawia mu listy bez tego (OrderViewSet.get_queryset).
  // Zapotrzebowania zostają otwarte jak dotąd.
  if (
    user.role === "employee" &&
    !user.has_granted_order_access &&
    location.pathname.startsWith("/orders")
  ) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

// Moduł Pozyskiwanie (leady) — CEO zawsze, pozostali po fladze nadanej przez CEO.
// Odwzorowuje backendowe HasLeadAccess w apps/leads/views.py; musi zostać zgodne
// z sekcją "Pozyskiwanie" w components/layout/Sidebar.tsx.
export function LeadsAccessGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!user.is_ceo && !user.has_lead_permission) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Skrzynka eop@ (poczta ENERGA w DLC_01). Bramkujemy WYŁĄCZNIE po
// `has_eop_mail_permission` — koordynatorzy przedsięwzięć ENERGA mają dostęp bez
// żadnej flagi, więc sprawdzanie tu `has_eop_mail_access` odcięłoby główną grupę
// odbiorców. Odwzorowuje backendowe HasEopMailAccess w apps/eop_mail/views.py.
export function EopMailGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!user.has_eop_mail_permission) return <Navigate to="/" replace />;
  return <Outlet />;
}
